import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiUrl } from '@/lib/api/index.js';
import type { components } from '@/lib/api/schema.gen.js';
import { queryKeys } from '@/lib/query-client.js';

export type Policy = components['schemas']['Policy'];
export type PolicyVersion = components['schemas']['PolicyVersion'];
export type PolicyCompliance = components['schemas']['PolicyCompliance'];
export type PolicyCategory = Policy['category'];
export type OutstandingPolicy = components['schemas']['OutstandingPolicies']['items'][number];

export function usePolicies(category?: PolicyCategory) {
  return useQuery({
    queryKey: queryKeys.policies.list({ category }),
    queryFn: () => api.get<{ items: Policy[] }>('/policies', { query: { category } }),
  });
}

/**
 * What the signed-in person still owes an acknowledgement on.
 *
 * Read on every page, not only after sign-in: HR can publish a mandatory
 * policy while somebody is mid-session, and the whole point of `mandatory` is
 * that it does not wait for their next sign-in.
 */
export function useOutstandingPolicies() {
  return useQuery({
    queryKey: queryKeys.policies.unacknowledged(),
    queryFn: () => api.get<{ items: OutstandingPolicy[] }>('/policies/outstanding'),
  });
}

export function usePolicyVersions(policyId: string | null) {
  return useQuery({
    queryKey: queryKeys.policies.versions(policyId ?? ''),
    queryFn: () => api.get<{ items: PolicyVersion[] }>(`/policies/${policyId}/versions`),
    enabled: Boolean(policyId),
  });
}

export function usePolicyCompliance(policyId: string | null) {
  return useQuery({
    queryKey: queryKeys.policies.compliance(policyId ?? ''),
    queryFn: () => api.get<PolicyCompliance>(`/admin/policies/${policyId}/compliance`),
    enabled: Boolean(policyId),
  });
}

export function useAcknowledgePolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (policyId: string) =>
      api.post<{ policyId: string }>(`/policies/${policyId}/acknowledge`),
    onSuccess: () => {
      // Both: the library shows an "acknowledged" tick and the prompt has to
      // stop asking. Invalidating only one leaves a banner nagging about
      // something the page behind it already shows as done.
      void queryClient.invalidateQueries({ queryKey: queryKeys.policies.all() });
    },
  });
}

export interface CreatePolicyInput {
  title: string;
  category: PolicyCategory;
  description?: string | null;
  mandatory: boolean;
}

export function useCreatePolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePolicyInput) => api.post<Policy>('/admin/policies', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.policies.all() });
    },
  });
}

export interface UpdatePolicyInput {
  policyId: string;
  title?: string;
  category?: PolicyCategory;
  description?: string | null;
  mandatory?: boolean;
}

function usePolicyMutation<TInput, TResult>(fn: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.policies.all() });
    },
  });
}

/** Renames or recategorises. Never touches the document — that is a new version. */
export function useUpdatePolicy() {
  return usePolicyMutation(({ policyId, ...changes }: UpdatePolicyInput) =>
    api.patch<Policy>(`/admin/policies/${policyId}`, changes),
  );
}

/** Takes it out of the library. The acknowledgements against it survive. */
export function useArchivePolicy() {
  return usePolicyMutation((policyId: string) =>
    api.post<Policy>(`/admin/policies/${policyId}/archive`),
  );
}

/**
 * Only for one that was never published — the server refuses the rest.
 *
 * A published policy is archived instead: the acknowledgements against it are
 * the record that somebody agreed to it.
 */
export function useDeletePolicy() {
  return usePolicyMutation((policyId: string) => api.delete<void>(`/admin/policies/${policyId}`));
}

export function useRestorePolicy() {
  return usePolicyMutation((policyId: string) =>
    api.post<Policy>(`/admin/policies/${policyId}/restore`),
  );
}

export interface PublishVersionInput {
  policyId: string;
  versionLabel: string;
  /** Optional on a first version — nothing has changed on one. */
  summary?: string;
  effectiveFrom: string;
  file: File;
}

/**
 * Publishes a new version.
 *
 * Sent as multipart rather than JSON, and deliberately not through the shared
 * `api` helper: that one sets `content-type: application/json`, and a
 * multipart body needs the browser to write the header itself so the boundary
 * matches the payload.
 */
export function usePublishVersion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ policyId, file, ...fields }: PublishVersionInput) => {
      const form = new FormData();
      form.append('versionLabel', fields.versionLabel);
      /**
       * Only when there is one.
       *
       * `FormData.append` stringifies whatever it is given, so an absent
       * summary was sent as the **four-letter word "undefined"** and stored
       * as the summary — every first version then told the company "What
       * changed: undefined" on the card they were being asked to
       * acknowledge. Found in the screenshot on orbit-ui#82, which was
       * about something else.
       */
      if (fields.summary?.trim()) form.append('summary', fields.summary.trim());
      form.append('effectiveFrom', fields.effectiveFrom);
      form.append('file', file);

      const response = await fetch(apiUrl(`/admin/policies/${policyId}/versions`), {
        method: 'POST',
        body: form,
        credentials: 'include',
      });

      const payload: unknown = await response.json().catch(() => null);
      if (!response.ok) {
        const error = (payload as { error?: { message?: string } } | null)?.error;
        throw new Error(error?.message ?? 'That version could not be published.');
      }
      return payload as PolicyVersion;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.policies.all() });
    },
  });
}

/**
 * Where a policy document is served from.
 *
 * Absolute, because the browser opens this in a tab itself rather than
 * fetching it through `api` — a relative path resolves against the SPA's
 * host and serves the app instead of the PDF.
 */
export function policyDocumentUrl(policyId: string, versionId: string): string {
  return apiUrl(`/policies/${policyId}/versions/${versionId}/document`);
}

/**
 * The document itself, as bytes.
 *
 * Not through `api`: that one asks for JSON and parses the reply as JSON, and
 * this reply is a PDF. The two things it does that matter here are done by
 * hand — the session cookie, and an error that says something.
 *
 * Fetched rather than framed by URL so that the viewer works on a split
 * origin (orbit-ui#171). In production the API is on another host, where a
 * cross-site `<iframe src>` carries no session cookie and is at the mercy of
 * the API's own frame headers; the page can frame the `blob:` URL this makes
 * whatever origin the bytes came from.
 */
export async function fetchPolicyDocument(policyId: string, versionId: string): Promise<Blob> {
  const response = await fetch(apiUrl(`/policies/${policyId}/versions/${versionId}/document`), {
    credentials: 'include',
  });

  if (!response.ok) {
    const payload: unknown = await response.json().catch(() => null);
    const error = (payload as { error?: { message?: string } } | null)?.error;
    throw new Error(error?.message ?? 'That document could not be opened.');
  }

  return response.blob();
}

export const POLICY_CATEGORY_LABELS: Record<PolicyCategory, string> = {
  GENERAL: 'General',
  CODE_OF_CONDUCT: 'Code of Conduct',
  IT_SECURITY: 'IT & Security',
  POSH: 'POSH',
  LEAVE: 'Leave',
  ATTENDANCE: 'Attendance',
  FINANCE: 'Finance',
  OTHER: 'Other',
};
